import { jsx, jsxs } from "react/jsx-runtime";
import { memo, useMemo, useCallback } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { useTranslation } from "react-i18next";
import "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { parseSelectTextOptionModels, useSelectTextQuiz, parseSelectTextContents } from "../hooks/use-select-text-quiz.js";
import { AnimatePresence } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/components/AnimatePresence/index.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
const StudentSelectBlank = memo(function StudentSelectBlank2({
  quizId,
  index,
  content,
  selectedOptionIdx,
  isOpen,
  isSolved,
  isViewSolved,
  isCorrectAnswer,
  shouldShowCorrectAnswers,
  correctAnswerIndex,
  onToggle
}) {
  var _a, _b, _c;
  const { t } = useTranslation("quiz");
  const options = useMemo(() => {
    return parseSelectTextOptionModels(content);
  }, [content]);
  const selectedOption = selectedOptionIdx !== null && ((_a = options[selectedOptionIdx]) == null ? void 0 : _a.isSelectable) ? options[selectedOptionIdx] : null;
  const showAsCorrect = isSolved && isCorrectAnswer;
  const showAsWrong = isSolved && !isCorrectAnswer && !shouldShowCorrectAnswers;
  const showCorrectOverride = shouldShowCorrectAnswers && isSolved && !isCorrectAnswer;
  const correctAnswer = ((_b = options[correctAnswerIndex ?? 0]) == null ? void 0 : _b.label) ?? ((_c = options.find((option) => option.isSelectable)) == null ? void 0 : _c.label) ?? "";
  const isCompleted = isSolved || isViewSolved;
  const hasValue = selectedOption !== null;
  return /* @__PURE__ */ jsx("span", { className: "inline-block my-1", children: /* @__PURE__ */ jsx(
    "button",
    {
      onClick: (e) => {
        e.stopPropagation();
        if (!isCompleted) onToggle(index);
      },
      disabled: isCompleted,
      className: cn(
        "rounded-2xl mx-2 font-medium inline-flex items-center justify-center gap-2 px-4 h-12 text-[24px]",
        // 완료 상태 (정답/오답/비활성)
        showCorrectOverride && "bg-lc-mint-200 text-lc-mint-900 border-none [box-shadow:unset] pointer-events-none",
        showAsCorrect && !showCorrectOverride && "bg-lc-mint-200 text-lc-mint-900 border-none [box-shadow:unset] pointer-events-none",
        showAsWrong && "bg-lc-red-200 text-lc-red-900 border-none [box-shadow:unset] pointer-events-none",
        isViewSolved && !isSolved && "border-none [box-shadow:unset] pointer-events-none",
        // 미완료 상태
        !isCompleted && !isOpen && !hasValue && "border-2 border-lc-gray-400 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]",
        !isCompleted && isOpen && "border-2 border-lc-lightblue-900 bg-lc-lightblue-200 min-w-[96px]",
        !isCompleted && !isOpen && hasValue && "border-2 border-lc-lightblue-900 bg-lc-lightblue-200"
      ),
      children: /* @__PURE__ */ jsx("span", { className: cn(isOpen && !hasValue && "invisible"), children: showCorrectOverride ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswer }) : hasValue ? /* @__PURE__ */ jsx(KatexText, { text: selectedOption.label }) : t("types.selectText.placeholder") })
    }
  ) }, `select-${quizId}-${index}`);
});
function SelectTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false
}) {
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    openDropdown,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit
  } = useSelectTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const allBlankOptions = useMemo(() => {
    return parseSelectTextContents(quiz.problem).map(parseSelectTextOptionModels);
  }, [quiz.problem]);
  const currentOptions = openDropdown !== null ? allBlankOptions[openDropdown] ?? [] : [];
  const currentSelectedIdx = openDropdown !== null && answers[openDropdown] !== "" ? parseInt(answers[openDropdown]) : null;
  const handlePanelSelect = useCallback((optIdx) => {
    if (openDropdown !== null) {
      handleSelect(openDropdown, optIdx);
    }
  }, [openDropdown, handleSelect]);
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && selectCount > 0) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${problemKey}`,
      (index, content) => {
        var _a, _b;
        const selectedOptionIdx = answers[index] !== "" ? parseInt(answers[index]) : null;
        const isOpen = openDropdown === index;
        const isCorrectByCorrects = (_a = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a[index];
        const isCorrectAnswer = isCorrectByCorrects !== void 0 && isCorrectByCorrects !== null ? isCorrectByCorrects : selectedOptionIdx === 0;
        const correctAnswerFromServer = (_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index];
        const correctAnswerIndex = typeof correctAnswerFromServer === "number" ? correctAnswerFromServer : typeof correctAnswerFromServer === "string" ? parseInt(correctAnswerFromServer) : null;
        return /* @__PURE__ */ jsx(
          StudentSelectBlank,
          {
            quizId,
            index,
            content,
            selectedOptionIdx,
            isOpen,
            isSolved,
            isViewSolved,
            isCorrectAnswer,
            shouldShowCorrectAnswers,
            correctAnswerIndex,
            onToggle: handleToggleDropdown
          },
          `select-${quizId}-${problemKey}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, openDropdown, isSolved, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, selectCount, quiz.additionalImages, solveResult == null ? void 0 : solveResult.corrects, solveResult == null ? void 0 : solveResult.correctAnswers, problemKey, handleToggleDropdown]);
  const isCompleted = isSolved || isViewSolved;
  const panelOpen = openDropdown !== null && !isCompleted;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.selectText.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimMedia: dimChoiceText
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-quiz-problem-text": true,
        className: cn(
          "text-2xl font-medium text-gray-700 leading-[48px]",
          dimChoiceText && "[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]"
        ),
        children: renderedProblem
      }
    ),
    /* @__PURE__ */ jsx(AnimatePresence, { children: panelOpen && currentOptions.length > 0 && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 20 },
        transition: { type: "tween", duration: 0.2, ease: "easeOut" },
        className: "w-full flex items-center justify-center gap-6 px-4",
        children: currentOptions.map((option) => {
          if (!option.isSelectable) return null;
          const isSelected = currentSelectedIdx === option.rawIndex;
          return /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handlePanelSelect(option.rawIndex),
              className: cn(
                "h-[92px] text-[20px] text-center overflow-hidden font-medium flex flex-wrap bg-white justify-center items-center border-2 rounded-3xl grow cursor-pointer select-none py-4 px-6",
                isSelected ? "text-lc-lightblue-900 border-lc-lightblue-900 [box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]" : "text-gray-900 border-lc-gray-400 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]"
              ),
              children: /* @__PURE__ */ jsx(KatexText, { text: option.label })
            },
            option.rawIndex
          );
        })
      }
    ) }),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  SelectTextStudent
};
//# sourceMappingURL=select-text-student.js.map
